import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { IPropertyEditorProps, IUIFieldProps, registerUIField } from "../../common";
import { containsExpressions, hasValue, parseValue } from "../../../../util";
import { IContextData } from "../../../../hooks/useContextData";
import { Loading } from "uxp/components";
import { Property, PropertyContainer, PropertyGroup, PropertyGroupTitle, PropertyTitle } from "../../ui-props/common";
import { WidgetDesignerPillInput } from "../../pill-input/PillInput";
import './Media.scss'

enum MediaType {
    JPEG = 'image/jpeg',
    PNG = 'image/png',
    GIF = 'image/gif',
    SVG = 'image/svg+xml',
    WEBP = 'image/webp',
    MP3 = 'audio/mp3',
    MP4 = 'video/mp4',
    WEBM = 'video/webm',
}

interface IMediaFieldProps {
    title: string, // field label
    description?: string
    url: string,
    mimeType?: 'auto' | MediaType
    viewMore?: {
        show: boolean,
        target?: '_blank' | '_self'
    }
    width: string,
    height: string
}

interface MediaDetails {
    isValid: boolean,
    error: string | null,
    type: string | null,
    mimeType: string | null,
    parsedUrl: string | null
}

function isValidMediaType(value: any): value is MediaType {
    return Object.values(MediaType).includes(value);
}


function getMediaType(mimeType: string): 'image' | 'audio' | 'video' | null {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType.startsWith('video/')) return 'video';
    return null;
}

async function processMediaFile(parsedUrl: string, contextData: IContextData): Promise<MediaDetails> {
    try {
        // Fetch the file from the URL
        const response = await fetch(parsedUrl);
        console.log('response ', response)
        if (!response.ok) {
            return { isValid: false, error: `Failed to fetch the file: ${response.statusText}`, type: null, mimeType: null, parsedUrl };
        }

        // Get the MIME type from the headers
        const mimeType = response.headers.get('Content-Type');
        if (!mimeType) {
            return { isValid: false, error: 'MIME type not found', type: null, mimeType: null, parsedUrl };
        }

        if (!isValidMediaType(mimeType)) {
            return { isValid: false, error: 'Unsupported File', type: null, mimeType: null, parsedUrl: null }
        }

        // Determine the type (image, audio, video) based on MIME type
        const type = getMediaType(mimeType);

        if (!type) {
            return { isValid: false, error: 'Unsupported media type', type: null, mimeType, parsedUrl };
        }

        // If all checks pass, return the media details
        return { isValid: true, error: null, type, mimeType, parsedUrl };
    } catch (e) {
        console.log('error ', e)
        return { isValid: false, error: `An error occurred: ${e.message}`, type: null, mimeType: null, parsedUrl: null };
    }
}
export const Field: React.FunctionComponent<IUIFieldProps<IMediaFieldProps>> = (props) => {

    let { title, description, url, mimeType, viewMore, width, height } = props.fieldProps || {}
    const [details, setDetails] = useState<MediaDetails>({ isValid: false, error: null, type: null, mimeType: null, parsedUrl: null })
    const [loading, setLoading] = useState<boolean>(false)

    const hasParsed = useRef(false)

    useEffect(() => {
        console.log('contextdata', props.contextData)
    }, [props.contextData])
    useEffect(() => {
        process()
    }, [url, mimeType, props.contextData])

    useEffect(() => {
        console.log('details', details, props.contextData)
    }, [details])

    function process() {

        const mType = parseValue(mimeType, props.contextData)

        if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
            setDetails({ isValid: true, error: null, type: 'image', mimeType: 'image/png', parsedUrl: 'https://static.iviva.com/lucy/logos/lucy-canvas.png' })
        }
        else {
            if (!hasValue(url)) {
                setDetails({ isValid: false, error: 'URL is empty', type: null, mimeType: null, parsedUrl: null })
                return
            }

            if (containsExpressions(url) && !props.contextData) {
                setDetails({ isValid: false, error: 'Context data not found', type: null, mimeType: null, parsedUrl: null })
                return
            }

            const parsedUrl = parseValue(url, props.contextData);
            console.log('parsed ', parsedUrl, containsExpressions(parsedUrl))

            if (containsExpressions(parsedUrl)) {
                setDetails({ isValid: false, error: 'Media not found', type: null, mimeType: null, parsedUrl: null })
                return
            }

            if (hasValue(mimeType) && isValidMediaType(mType)) {
                setDetails({ isValid: true, parsedUrl: parsedUrl, type: getMediaType(mType), mimeType: mType, error: null })
                hasParsed.current = true
                return
            }

            if (loading || (hasParsed.current && details?.isValid)) return // prevent sending multiple requests // to stop flickering 
            setLoading(true)
            processMediaFile(parsedUrl, props.contextData)
                .then(res => {
                    setDetails(res)
                    hasParsed.current = true
                })
                .catch(e => {

                })
                .finally(() => {
                    setTimeout(() => { setLoading(false) }, 300)
                })
        }
    }

    function renderMedia() {

        if (details?.type == 'image') {
            return <img src={details?.parsedUrl} />
        }
        if (details?.type == 'video') {
            return <video controls >
                <source src={details?.parsedUrl} type={details.mimeType} />
            </video>
        }
        if (details?.type == 'audio') {
            return <audio controls >
                <source src={details?.parsedUrl} type={details.mimeType} />
            </audio>
        }

        return null
    }


    const styles: React.CSSProperties = { width: parseValue(width, props.contextData), height: parseValue(height, props.contextData) }

    return <div className={classNames('ui-field', props.mode)} >
        {hasValue(title) && <div className='ui-field-label'>{title}</div>}
        {hasValue(description) && <div className='ui-field-description'>{description}</div>}

        {
            loading
                ? <Loading />
                : <div className="media-field-container" style={styles} >
                    {
                        details?.isValid
                            ? <div className="media-field-content-container">
                                {renderMedia()}
                            </div>
                            : <div className="media-field-error-container">{details.error || ''}</div>
                    }
                </div>
        }

    </div>

}


interface IMediaFieldProperty {
    title: string,
    description: string,
    url: string,
    mimeType: string,
    width: string,
    height: string

}
export const PropertyEditor: React.FunctionComponent<IPropertyEditorProps<IMediaFieldProperty>> = (props) => {

    const { uiProps, onChange, contextData } = props

    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>Media Field</PropertyGroupTitle>

            <Property>
                <PropertyTitle> Title </PropertyTitle>
                <WidgetDesignerPillInput
                    value={uiProps.title || ''}
                    onChange={s => onChange(Object.assign({}, uiProps, { title: s }))}
                    contextData={contextData}
                />
            </Property>

            <Property>
                <PropertyTitle> Description </PropertyTitle>
                <WidgetDesignerPillInput
                    value={uiProps.description || ''}
                    onChange={s => onChange(Object.assign({}, uiProps, { description: s }))}
                    contextData={contextData}
                />
            </Property>

            <Property>
                <PropertyTitle> URL </PropertyTitle>
                <WidgetDesignerPillInput
                    value={uiProps.url || ''}
                    onChange={s => onChange(Object.assign({}, uiProps, { url: s }))}
                    contextData={contextData}
                />
            </Property>

            <Property>
                <PropertyTitle> Type </PropertyTitle>
                <WidgetDesignerPillInput
                    value={uiProps.mimeType || ''}
                    onChange={s => onChange(Object.assign({}, uiProps, { mimeType: s }))}
                    contextData={contextData}
                />
            </Property>

            <Property>
                <PropertyTitle> Width </PropertyTitle>
                <WidgetDesignerPillInput
                    value={uiProps.width || '100%'}
                    onChange={s => onChange(Object.assign({}, uiProps, { width: s }))}
                    contextData={contextData}
                />
            </Property>

            <Property>
                <PropertyTitle> Height </PropertyTitle>
                <WidgetDesignerPillInput
                    value={uiProps.height || '100px'}
                    onChange={s => onChange(Object.assign({}, uiProps, { height: s }))}
                    contextData={contextData}
                />
            </Property>

        </PropertyGroup>
    </PropertyContainer>
}


registerUIField({
    id: 'media',
    category: 'cards-media',
    description: 'Image/Video/Audio Field',
    field: Field,
    isInputField: false,
    propertyEditor: PropertyEditor,
    defaultProps: {
        title: 'Media File',
        description: '',
        url: null,
        width: '100%',
        height: '100px',
        mimeType: 'auto'
    }
});